import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import { Settings, Plus, Trash2, Save, Clock, MapPin } from 'lucide-react';
import { DeliveryZoneEditor } from './DeliveryZoneEditor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const PlatformConfigTab = ({ token }) => {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const headers = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/platform-config`, {
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        credentials: 'include',
      });
      if (r.ok) setCfg(await r.json());
    } catch (e) { console.debug('platform-config load failed', e); }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/platform-config`, {
        method: 'PUT', headers: headers(), credentials: 'include',
        body: JSON.stringify({
          delivery_pricing: cfg.delivery_pricing,
          platform_hours: cfg.platform_hours,
          delivery_zones: cfg.delivery_zones,
        }),
      });
      toast[r.ok ? 'success' : 'error'](r.ok ? 'Guardado' : 'Erro a guardar');
    } catch (e) { toast.error('Erro de rede'); }
    setSaving(false);
  };

  if (loading || !cfg) return <p className="p-4 text-sm text-[#9FA2B4]">A carregar...</p>;

  const tiers = cfg.delivery_pricing?.tiers || [];
  const beyond = cfg.delivery_pricing?.beyond_price ?? 12;
  const hours = cfg.platform_hours || {};

  const updateTier = (i, field, v) => {
    const next = [...tiers];
    next[i] = { ...next[i], [field]: parseFloat(v) || 0 };
    setCfg({ ...cfg, delivery_pricing: { ...cfg.delivery_pricing, tiers: next } });
  };
  const addTier = () => {
    const next = [...tiers, { max_km: (tiers.at(-1)?.max_km || 0) + 1, price: 0 }];
    setCfg({ ...cfg, delivery_pricing: { ...cfg.delivery_pricing, tiers: next } });
  };
  const removeTier = (i) => {
    const next = tiers.filter((_, idx) => idx !== i);
    setCfg({ ...cfg, delivery_pricing: { ...cfg.delivery_pricing, tiers: next } });
  };

  return (
    <div className="space-y-6" data-testid="platform-config-tab">
      {/* Delivery pricing */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings size={18} /> Preços de Entrega (por distância)
          </CardTitle>
          <p className="text-xs text-[#9FA2B4] mt-1">
            Aplica-se a todos os serviços (comida, gelo, shisha). Distância em linha reta (GPS).
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          {tiers.map((t, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`tier-row-${i}`}>
              <span className="text-sm w-16 text-[#9FA2B4]">até</span>
              <Input
                type="number" step="0.1" value={t.max_km}
                onChange={(e) => updateTier(i, 'max_km', e.target.value)}
                className="w-24" data-testid={`tier-km-${i}`}
              />
              <span className="text-sm">km →</span>
              <Input
                type="number" step="0.01" value={t.price}
                onChange={(e) => updateTier(i, 'price', e.target.value)}
                className="w-28" data-testid={`tier-price-${i}`}
              />
              <span className="text-sm">€</span>
              <Button variant="ghost" size="sm" onClick={() => removeTier(i)} className="text-red-500">
                <Trash2 size={14} />
              </Button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={addTier} className="rounded-full" data-testid="add-tier">
            <Plus size={14} className="mr-1" /> Adicionar escalão
          </Button>
          <div className="flex items-center gap-2 pt-2 border-t">
            <span className="text-sm w-48">Acima do último escalão:</span>
            <Input
              type="number" step="0.01" value={beyond}
              onChange={(e) => setCfg({
                ...cfg, delivery_pricing: { ...cfg.delivery_pricing, beyond_price: parseFloat(e.target.value) || 0 },
              })}
              className="w-28" data-testid="beyond-price"
            />
            <span className="text-sm">€</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm w-48">Extra por km (dentro da zona):</span>
            <Input
              type="number" step="0.01"
              value={cfg.delivery_pricing?.extra_km_price ?? 2}
              onChange={(e) => setCfg({
                ...cfg, delivery_pricing: { ...cfg.delivery_pricing, extra_km_price: parseFloat(e.target.value) || 0 },
              })}
              className="w-28" data-testid="extra-km-price"
            />
            <span className="text-sm">€/km</span>
          </div>
          <p className="text-xs text-[#9FA2B4]">
            Clientes dentro da zona além do último escalão pagam: último escalão + €/km extra por cada km iniciado.
            Ex.: 6km = €{((cfg.delivery_pricing?.tiers?.at(-1)?.price ?? 12) + (cfg.delivery_pricing?.extra_km_price ?? 2)).toFixed(2)},
            7km = €{((cfg.delivery_pricing?.tiers?.at(-1)?.price ?? 12) + 2 * (cfg.delivery_pricing?.extra_km_price ?? 2)).toFixed(2)}
          </p>
        </CardContent>
      </Card>

      {/* Delivery zones (polygons) */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin size={18} /> Zonas de Entrega (mapa)
          </CardTitle>
          <p className="text-xs text-[#9FA2B4] mt-1">
            Cria várias zonas (Zona 1, Zona 2...). Cliente DENTRO de uma zona ativa pode pedir e paga segundo a regra dessa zona (por km ou preço fixo). Fora de todas as zonas fica bloqueado. Se as zonas se sobrepuserem, aplica-se a primeira da lista.
          </p>
        </CardHeader>
        <CardContent>
          <DeliveryZoneEditor
            zones={cfg.delivery_zones || []}
            onChange={(zs) => setCfg({ ...cfg, delivery_zones: zs })}
          />
        </CardContent>
      </Card>

      {/* Platform hours */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock size={18} /> Horário da Plataforma (global)
          </CardTitle>
          <p className="text-xs text-[#9FA2B4] mt-1">
            Mesmo que o restaurante esteja aberto, se a plataforma estiver fechada não se aceitam pedidos.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-3">
            <Switch
              checked={!!hours.is_enabled}
              onCheckedChange={(v) => setCfg({ ...cfg, platform_hours: { ...hours, is_enabled: v } })}
              data-testid="platform-hours-toggle"
            />
            <Label>Aplicar horário (se desligado, 24/7)</Label>
          </div>
          <div className="flex items-center gap-3">
            <Label className="w-28">Abertura</Label>
            <Input
              type="time" value={hours.open || '08:00'}
              onChange={(e) => setCfg({ ...cfg, platform_hours: { ...hours, open: e.target.value } })}
              className="w-32" data-testid="platform-open"
            />
            <Label className="w-28">Fecho</Label>
            <Input
              type="time" value={hours.close || '23:59'}
              onChange={(e) => setCfg({ ...cfg, platform_hours: { ...hours, close: e.target.value } })}
              className="w-32" data-testid="platform-close"
            />
          </div>
        </CardContent>
      </Card>

      <Button
        onClick={save} disabled={saving}
        className="bg-[#FF6B35] hover:bg-[#E85D2A] rounded-full"
        data-testid="save-platform-config"
      >
        <Save size={16} className="mr-2" /> {saving ? 'A guardar...' : 'Guardar Configurações'}
      </Button>
    </div>
  );
};

export default PlatformConfigTab;
